import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon, Video } from 'lucide-react';
import CTA from '../../components/CTA';

const MagherafeltAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Magherafelt | Rural Market Town Stress Hypnotherapy</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Magherafelt. Online sessions recommended for convenience. Rural stress, farming community support, anxiety treatment. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Magherafelt, stress management Magherafelt Mid Ulster, rural anxiety support, farming community mental health, hypnotherapy Magherafelt, online therapy County Londonderry" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/magherafelt-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Magherafelt | Rural Market Town Stress Hypnotherapy" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Magherafelt from our south Belfast clinic. Rural stress, farming community support, anxiety treatment. Online sessions recommended." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety and Stress Treatment Magherafelt",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Magherafelt via online sessions and Belfast clinic. Specializing in rural isolation, farming community stress, market town business pressures, anxiety disorders, and confidential support for Mid Ulster.",
            "url": "https://www.sharonclare-hypnotherapy.com/magherafelt-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Magherafelt",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Belfast",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How can Magherafelt residents access anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Online sessions are the recommended option for Magherafelt residents - you receive the same professional, evidence-based anxiety treatment from the comfort of your home without any travel required. For those who prefer in-person sessions, the Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ is available, though it is a significant distance from Magherafelt. Most Magherafelt clients choose online sessions for practical convenience and rural accessibility."
                }
              },
              {
                "@type": "Question",
                "name": "Can I do anxiety treatment online instead of driving to Belfast?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely - online sessions are recommended as the primary option for Magherafelt residents. I offer secure online hypnotherapy sessions via video call that provide exactly the same professional, evidence-based anxiety treatment as in-person sessions. Online therapy is ideal for those in rural areas, with caring responsibilities, farm work schedules, or anyone who wants the convenience and privacy of attending from home."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for rural stress and isolation anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, solution-focused hypnotherapy is highly effective for anxiety related to rural isolation and the challenges of living in market towns. Many Magherafelt residents experience stress from limited access to specialist services, distance from support networks, and the unique pressures of rural living. Hypnotherapy helps by calming the anxiety response, building resilience, improving sleep, and providing confidential professional support that may not be locally available."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help the farming community with stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for farming community stress and agricultural anxiety. Many Magherafelt area farmers deal with financial uncertainty tied to weather and markets, physical isolation during busy seasons, multi-generational farm pressures, and mental health stigma in agricultural communities. Solution-focused hypnotherapy helps manage this stress, build resilience, improve sleep despite irregular hours, and develop healthier coping strategies without judgment."
                }
              },
              {
                "@type": "Question",
                "name": "How does living in a small market town affect anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Living in a small market town like Magherafelt can contribute to anxiety through several factors: limited anonymity in close-knit communities where privacy concerns may prevent people seeking local help, economic pressures from competition with larger towns, distance from specialist services, and rural isolation. While community support can be strong, the lack of confidential professional services locally can make it difficult to address mental health challenges. Online hypnotherapy provides confidential support outside local networks."
                }
              },
              {
                "@type": "Question",
                "name": "Is anxiety treatment confidential in a small town?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. All sessions are completely confidential and protected by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Online sessions provide extra privacy for small town residents who may be concerned about being seen attending therapy locally. Whether you attend online from home in Magherafelt or in-person at the Belfast clinic, your privacy is fully protected. Nothing discussed in sessions is shared with anyone without your explicit consent."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Magherafelt
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy for Mid Ulster's traditional market town - online sessions recommended
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <MapPin className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700 font-semibold">
                  In person at the Belfast clinic on the Ormeau Road or the Lisburn clinic at The Arzen Collective
                </p>
              </div>
              <div className="flex items-center justify-center space-x-3 text-gray-600">
                <Video className="w-5 h-5" />
                <p className="text-base">
                  Online sessions also available any day — clinic days and times vary
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Magherafelt */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Magherafelt
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Magherafelt and struggling with anxiety or stress, you're not alone. As Mid Ulster's largest traditional market town with a population of around 9,000, Magherafelt maintains its agricultural heritage and close-knit community character. While this creates strong social bonds, it also presents unique mental health challenges that urban residents don't face - rural isolation, limited local access to specialist services, privacy concerns in small-town networks, and the specific stressors of farming and market town life.
              </p>
              <p>
                Many Magherafelt residents experience anxiety related to rural isolation and distance from professional mental health support. The nearest specialist services are often 30-40 miles away, creating a significant barrier to regular treatment. For farmers and agricultural families, there's the added stress of financial uncertainty tied to weather and markets, physical isolation during busy seasons, multi-generational farm pressures, and the stigma around mental health that still exists in agricultural communities. The attitude of "just get on with it" can make it incredibly difficult to admit you're struggling or reach out for help.
              </p>
              <p>
                For those running small businesses in Magherafelt's traditional market town, there's anxiety from competition with larger regional towns like Cookstown and online shopping, economic uncertainty, and the pressure of keeping family businesses going. Living in a close-knit community of 9,000 people means limited anonymity - you may worry about being seen seeking help or the stigma of admitting mental health struggles when everyone knows everyone. This privacy concern can be a significant barrier to accessing the support you need.
              </p>
              <p>
                This is where online hypnotherapy becomes particularly valuable for Magherafelt residents. While my Belfast clinic is approximately 30 miles from Magherafelt (40-45 minutes via the A29), and in-person sessions are certainly available if you prefer that option, I strongly recommend online sessions as the primary approach. Online therapy provides exactly the same professional, evidence-based anxiety treatment without the travel time, with complete confidentiality in your own home, and without concerns about being seen attending therapy. For rural communities, this accessibility can be life-changing. You get specialist support that may not be available locally, with the convenience and privacy of attending from home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Magherafelt Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Difficulty falling or staying asleep',
                    'Racing thoughts, especially at night',
                    'Physical symptoms: tight chest, pounding heart, tense shoulders',
                    'Constant sense of unease or dread',
                    'Avoiding situations out of fear or worry',
                    'Feeling overwhelmed by everyday tasks',
                    'Difficulty concentrating or making decisions',
                    'Irritability and mood swings'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Magherafelt-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Rural isolation and distance from services',
                    'Farming community financial uncertainty',
                    'Weather and market stress for agriculture',
                    'Physical isolation during busy farm seasons',
                    'Small-town privacy concerns and stigma',
                    'Market town business competition pressures',
                    'Limited anonymity in close-knit community',
                    'Mental health stigma in agricultural areas'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                When anxiety and stress dominate your life in a rural community, it can feel particularly isolating. The physical symptoms - racing heart, tight chest, poor sleep - combined with the emotional burden and the difficulty of accessing help locally can make the problem feel insurmountable. But you don't have to struggle alone. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you regain control and build lasting calm - all from the privacy and convenience of your own home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety: What's Really Happening
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Anxiety is incredibly common - around 1 in 4 people in the UK will experience an anxiety disorder at some point in their lives. But in rural communities, it often goes unaddressed due to stigma, distance from services, and the attitude that you should just "cope" or "get on with it." Anxiety is your brain's natural alarm system going into overdrive, interpreting everyday situations - financial worries, weather forecasts, market prices, social events - as serious threats and triggering your fight-or-flight response even when there's no immediate danger.
              </p>
              <p>
                When you're anxious, your brain releases stress hormones like cortisol and adrenaline, preparing your body to respond to a perceived threat. This is why you experience physical symptoms - racing heart, tight chest, shallow breathing, muscle tension, digestive issues, feeling on edge. Your brain is literally preparing you to fight or run away, even when you're just lying in bed worrying about next season's harvest or checking weather forecasts obsessively.
              </p>
              <p>
                Anxiety disorders include generalized anxiety disorder (persistent, excessive worry about finances, family, health, farm work), social anxiety (fear of judgment in close-knit communities), panic disorder (sudden, intense panic attacks), and specific phobias. Many people, especially in farming communities, experience chronic stress that doesn't fit neatly into diagnostic categories but still significantly impacts quality of life. What matters is not the label, but getting the help you need.
              </p>
              <p>
                The good news is that anxiety is highly treatable, even in rural areas where specialist services aren't locally available. Online hypnotherapy provides the same professional, evidence-based treatment as in-person sessions, but with the accessibility and privacy that rural residents need. Your brain created these anxious patterns through repeated stress, and your brain can learn new, healthier patterns. Solution-focused hypnotherapy works by calming the overactive stress response, helping you understand triggers, building resilience, and creating lasting change in how you respond to stress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response so panic and tension reduce, and clarity returns. It's not about dwelling on the past or analyzing every source of stress - it's about building the resilience and calm you need to move forward, whether you're dealing with farm pressures, business stress, or general anxiety.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms like racing heart, tight chest, and tense muscles. Your body learns to relax again, even during stressful farm seasons or market uncertainties.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Rewire Thought Patterns',
                  text: 'Build new habits of thought and behavior that support resilience and wellbeing. Replace anxious, catastrophic thinking about finances or weather with calmer, more balanced responses to uncertainty.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Create a calmer pre-sleep routine and quiet the racing mind that keeps you awake despite exhaustion. Wake feeling more refreshed and better equipped to handle the demands of farm work or business pressures.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-white rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl text-center">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety disorder',
                    'Social anxiety in close-knit communities',
                    'Panic attacks and panic disorder',
                    'Farming community stress and isolation',
                    'Financial anxiety from market uncertainty'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Market town business pressures',
                    'Rural isolation and loneliness',
                    'Sleep problems from worry and stress',
                    'Weather-related anxiety for farmers',
                    'Small-town stigma and privacy concerns'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Sharon's Approach to Anxiety Treatment
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. My background in nursing means I understand the medical and neurological side of anxiety - the brain chemistry, the physical symptoms, the interactions with other health conditions. But more importantly, I understand the human side: the exhaustion, the stigma, the isolation of struggling in a rural community, and the courage it takes to reach out for help.
              </p>
              <p>
                I use solution-focused hypnotherapy, an evidence-based approach that combines talking therapy with hypnosis. Each session begins with structured conversation about your week, focusing on what's working, what you want to achieve, and practical steps forward. This isn't about dwelling on problems or analyzing every source of stress - it's about building momentum toward positive change. Then we move into hypnosis, a deeply relaxed state where your subconscious mind becomes more receptive to positive suggestions and new patterns of thinking.
              </p>
              <p>
                Hypnosis isn't sleep, and it's not stage hypnosis. You remain completely aware and in control throughout. It's simply a state of focused attention and deep relaxation - similar to being absorbed in a good book or driving a familiar route on autopilot. In this relaxed state, we work with your subconscious mind to calm the overactive anxiety response, build resilience to uncertainty, and create lasting change in how you think about and respond to stressful situations.
              </p>
              <p>
                Most clients notice significant improvements within 4-6 sessions, though everyone's journey is different. Some people feel noticeably calmer after just one or two sessions. Others need a bit more time to build lasting change, especially if anxiety has been present for years or is deeply tied to farm work and rural life. What matters is that you're moving forward at your own pace, supported by evidence-based treatment that addresses both the symptoms and the underlying causes of anxiety. You don't have to struggle alone with anxiety and stress. Help is available, accessible, and confidential - and lasting change is absolutely possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Accessing Anxiety Treatment from Magherafelt
            </h2>

            <div className="bg-white rounded-2xl p-8 mb-8 border-2 border-accent-200">
              <div className="flex items-start space-x-4 mb-6">
                <Video className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Recommended)</h3>
                  <p className="text-gray-700 mb-4">
                    For Magherafelt residents, I strongly recommend online sessions as the primary option. Online hypnotherapy provides exactly the same professional, evidence-based anxiety treatment as in-person sessions, but with significant advantages for rural communities:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>No travel required - save 80-90 minutes per session (40-45 min each way)</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Complete privacy in your own home - no concerns about being seen attending therapy</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Flexible scheduling around farm work or business commitments</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Access specialist treatment that may not be available locally</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <p className="text-gray-700">
                  Online sessions use secure video call technology. All you need is a quiet space in your home, a stable internet connection, and a device with a camera (smartphone, tablet, or computer). The treatment is just as effective as in-person sessions - only the location changes. Many rural clients actually prefer online therapy because of the convenience, privacy, and time savings.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">In-Person at Belfast Clinic (If Preferred)</h3>
                  <p className="text-gray-700 mb-2">
                    While online sessions are recommended, in-person appointments are available if you prefer face-to-face treatment:
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Address:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Note:</span> Most Magherafelt clients choose online sessions for convenience due to the distance
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Mid Ulster Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Farming has always been stressful, but the last few years broke me - weather, markets, money worries keeping me awake at night, feeling completely isolated during busy seasons. I didn't want to admit I was struggling, and there was no way I could drive 30-40 miles for therapy regularly. Sharon's online sessions were a lifesaver. She understands the farming pressures without judgment. After six sessions, I'm sleeping properly, I can handle the uncertainty better, and I've got tools to manage the stress. I wish I'd done this years ago."
                </p>
                <p className="font-semibold text-gray-900">P.M., Mid Ulster - Farming Community Stress</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The anxiety got so bad I couldn't leave the house - racing heart, couldn't breathe, convinced something terrible was going to happen. Living in a small town where everyone knows everyone, I was terrified of people finding out I was struggling. Online therapy with Sharon gave me the privacy I needed. After five sessions, the panic attacks have stopped, I'm functioning again, and I feel like myself for the first time in years. Being able to do it from home made all the difference."
                </p>
                <p className="font-semibold text-gray-900">S.D., County Londonderry - Panic Attacks and Rural Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Running a small business in a market town, competing with online shopping and bigger stores, I was completely burnt out. Constant worry about money, terrible sleep, snapping at my family. I was embarrassed to seek help locally. Sharon's online hypnotherapy helped me manage the business stress and get some perspective. I'm still facing the same challenges, but I handle them so much better now. The anxiety doesn't control me like it used to. I can sleep again and actually enjoy my life."
                </p>
                <p className="font-semibold text-gray-900">M.L., Mid Ulster - Market Town Business Stress</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Magherafelt Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How can Magherafelt residents access anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Online sessions are the recommended option for Magherafelt residents - you receive the same professional, evidence-based anxiety treatment from the comfort of your home without any travel required. For those who prefer in-person sessions, the Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ is available, though it is a significant distance from Magherafelt. Most Magherafelt clients choose online sessions for practical convenience and rural accessibility.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I do anxiety treatment online instead of driving to Belfast?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely - online sessions are recommended as the primary option for Magherafelt residents. I offer secure online hypnotherapy sessions via video call that provide exactly the same professional, evidence-based anxiety treatment as in-person sessions. Online therapy is ideal for those in rural areas, with caring responsibilities, farm work schedules, or anyone who wants the convenience and privacy of attending from home.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for rural stress and isolation anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, solution-focused hypnotherapy is highly effective for anxiety related to rural isolation and the challenges of living in market towns. Many Magherafelt residents experience stress from limited access to specialist services, distance from support networks, and the unique pressures of rural living. Hypnotherapy helps by calming the anxiety response, building resilience, improving sleep, and providing confidential professional support that may not be locally available.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help the farming community with stress?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for farming community stress and agricultural anxiety. Many Magherafelt area farmers deal with financial uncertainty tied to weather and markets, physical isolation during busy seasons, multi-generational farm pressures, and mental health stigma in agricultural communities. Solution-focused hypnotherapy helps manage this stress, build resilience, improve sleep despite irregular hours, and develop healthier coping strategies without judgment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does living in a small market town affect anxiety?
                </h3>
                <p className="text-gray-700">
                  Living in a small market town like Magherafelt can contribute to anxiety through several factors: limited anonymity in close-knit communities where privacy concerns may prevent people seeking local help, economic pressures from competition with larger towns, distance from specialist services, and rural isolation. While community support can be strong, the lack of confidential professional services locally can make it difficult to address mental health challenges. Online hypnotherapy provides confidential support outside local networks.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is anxiety treatment confidential in a small town?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. All sessions are completely confidential and protected by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Online sessions provide extra privacy for small town residents who may be concerned about being seen attending therapy locally. Whether you attend online from home in Magherafelt or in-person at the Belfast clinic, your privacy is fully protected. Nothing discussed in sessions is shared with anyone without your explicit consent.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm"
        subtitle="Take the first step toward less anxiety, less stress, and more peace. Book a free consultation today - online sessions available for complete convenience and privacy."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default MagherafeltAnxietyStress;
